/* Stubs for Phase 1b surfaces. Data model is already behind them —
   this is the first visual pass + "coming next" message so the Phase 1
   navigation is complete. */
import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  FileText, Handshake, Users2, Link as LinkIcon, Star, Brain, BarChart3, Loader2,
} from "lucide-react";

function Stub({ icon: Icon, title, blurb, note, testId }) {
  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid={testId}>
      <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2">
        <Icon className="h-7 w-7 text-indigo-400" /> {title}
      </h1>
      <p className="text-slate-400 text-sm mt-1">{blurb}</p>
      <Card className="bg-slate-900/60 border-slate-800 border-dashed mt-6">
        <CardContent className="p-10 text-center">
          <Icon className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <p className="text-white font-bold">Backend models live. UI arrives in Phase 1b.</p>
          <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">{note}</p>
          <Link to="/growth" className="text-indigo-400 hover:text-indigo-300 text-sm font-bold inline-block mt-4">← Back to Command Center</Link>
        </CardContent>
      </Card>
    </div>
  );
}

export const GrowthContent = () => (
  <Stub icon={FileText} title="Content Engine" testId="stub-content"
    blurb="SEO blog drafts, free-calculator specs, short-form video scripts, weekly social-pack."
    note="The AI drafts posts around high-intent service-business keywords; the operator reviews and publishes manually in Phase 1 (no WordPress/Buffer integration until Phase 2)." />
);
export const GrowthPartnerships = () => (
  <Stub icon={Handshake} title="Partnerships" testId="stub-partnerships"
    blurb="CRMs, review platforms, invoicing tools, scheduling tools, podcasts, associations."
    note="Database holds Jobber, Housecall Pro, ServiceTitan, Service Fusion, WorkWave, Service Autopilot, Jobox, ThermoGrid, Workiz, Kickserv + top-20 podcasts + top-10 industry associations with drafted outreach." />
);
export const GrowthCommunities = () => (
  <Stub icon={Users2} title="Communities" testId="stub-communities"
    blurb="Per-community rules, membership tracking, posting workflow. Phase 2."
    note="Reddit RSS sunsets Nov 13, 2026 — this lane revisits post-sunset via the paid Apify actor. Facebook Groups has no public API; drafts go to the owner's social-post queue for manual posting." />
);
export const GrowthAffiliates = () => (
  <Stub icon={LinkIcon} title="Affiliates" testId="stub-affiliates"
    blurb="Coded links, commission tracking, payout queue. Phase 2."
    note="Phase 1 captures UTM attribution on every signup; a referral code lands post-50 paid customers." />
);
export const GrowthTestimonials = () => (
  <Stub icon={Star} title="Testimonials" testId="stub-testimonials"
    blurb="Flagged candidates, drafted asks, stored responses with permission."
    note="Reads the Customer Activation Queue for activated, consistent, multi-employee, multi-week customers. Never auto-publishes." />
);
export const GrowthInsights = () => (
  <Stub icon={Brain} title="AI Insights" testId="stub-insights"
    blurb="Daily brief, weekly executive report, lane-level ROI rollup."
    note="Daily brief ships three recommended actions linked to records. Weekly ships growth vs prior week, best/worst lane, bottlenecks, suggested experiments." />
);

export function GrowthAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/growth/analytics/funnel").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-10 text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>;
  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="growth-analytics">
      <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2"><BarChart3 className="h-7 w-7 text-indigo-400" /> Analytics</h1>
      <p className="text-slate-400 text-sm mt-1">High-level funnel. Full lane-level ROI, cohort retention, and date filters land in Phase 1b.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        {[
          ["users_total", "Users"], ["companies_total", "Companies"],
          ["companies_trialing", "Trialing"], ["companies_active", "Paid"],
          ["opportunities_total", "Opportunities"], ["leads_total", "Leads"],
          ["leads_paid", "Leads paid"],
        ].map(([k, label]) => (
          <Card key={k} className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
              <p className="text-3xl font-extrabold text-white mt-1">{data[k]?.toLocaleString?.() ?? data[k]}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <Card className="bg-slate-900/60 border-slate-800"><CardContent className="p-4"><p className="text-xs uppercase tracking-widest text-slate-500">Opportunity → Lead</p><p className="text-2xl font-extrabold text-indigo-300 mt-1">{Math.round(data.opp_to_lead_rate * 100)}%</p></CardContent></Card>
        <Card className="bg-slate-900/60 border-slate-800"><CardContent className="p-4"><p className="text-xs uppercase tracking-widest text-slate-500">Lead → Paid</p><p className="text-2xl font-extrabold text-emerald-300 mt-1">{Math.round(data.lead_to_paid_rate * 100)}%</p></CardContent></Card>
      </div>
    </div>
  );
}
